import { getLocaleString } from '@assets/i18n/config';

export type FormatLastRefreshedOptions = {
  /**
   * BCP 47 locale string, e.g. 'en-US', 'fr-CA'.
   * Defaults to the current user's locale via `getLocaleString()`.
   */
  locale?: string;
  /**
   * IANA time zone identifier, e.g. 'America/New_York'.
   * Defaults to `undefined` (browser-local time zone).
   * Pass `'America/New_York'` if product requires ET regardless of viewer location.
   */
  timeZone?: string;
};

/**
 * Formats a `Date` for the Drift Queue "last refreshed" header line.
 *
 * The returned string includes date, time, and a short timezone token emitted
 * by the runtime ICU bundle via `Intl.DateTimeFormat`. The exact format depends
 * on the locale and runtime:
 *
 * - **en-US, America/New_York (DST active):** `"May 11, 2025, 6:40 AM ET"`
 * - **fr-CA, America/New_York (DST active):** `"11 mai 2025, 06 h 40 HAE"`
 *
 * @remarks
 * Timezone abbreviations come from the runtime ICU bundle and are not stable
 * across Node/ICU versions. Uses `shortGeneric` so Eastern Time renders as `ET`
 * rather than `EDT`/`EST`.
 * Tests should match loosely with regex rather than exact string equality.
 *
 * @param date - The timestamp to format.
 * @param options - Optional locale and IANA time zone overrides.
 * @returns A locale-formatted date+time+timezone string.
 */
export function formatLastRefreshed(
  date: Date,
  options?: FormatLastRefreshedOptions,
): string {
  const locale = options?.locale ?? getLocaleString();

  const intlOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'shortGeneric',
    ...(options?.timeZone ? { timeZone: options.timeZone } : {}),
  };

  return new Intl.DateTimeFormat(locale, intlOptions).format(date);
}
